import { createContext, useContext, useMemo, useRef, useState } from "react";
import type { Dispatch, ReactNode, RefObject, SetStateAction } from "react";
import type { NavLabel } from "../toolbar/SlideToolbar";

interface SlideEditorContextType {
  canvasRef: RefObject<HTMLDivElement>;
  activePanel: NavLabel | null;
  setActivePanel: Dispatch<SetStateAction<NavLabel | null>>;
  isLiveView: boolean;
  setIsLiveView: (value: boolean) => void;
  isGridVisible: boolean;
  setIsGridVisible: Dispatch<SetStateAction<boolean>>;
}

const SlideEditorContext = createContext<SlideEditorContextType | null>(null);

export function SlideEditorProvider({ children }: { children: ReactNode }) {
  const canvasRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const [activePanel, setActivePanel] = useState<NavLabel | null>(null);
  const [isLiveView, setIsLiveView] = useState<boolean>(false);
  const [isGridVisible, setIsGridVisible] = useState<boolean>(true);

  const value = useMemo(
    () => ({
      canvasRef,
      activePanel,
      setActivePanel,
      isLiveView,
      setIsLiveView,
      isGridVisible,
      setIsGridVisible,
    }),
    [canvasRef, activePanel, isLiveView, isGridVisible]
  );

  return <SlideEditorContext.Provider value={value}>{children}</SlideEditorContext.Provider>;
}

export function useSlideEditorContext() {
  const context = useContext(SlideEditorContext);
  if (!context) {
    throw new Error("useSlideEditorContext must be used within SlideEditorProvider");
  }
  return context;
}
